import { AlertTriangle, TrendingUp } from "lucide-react";
import { Button } from "@headerhawk/ui";
import { UsageMeter } from "./usage-meter";
import {
  useUsage,
  formatReportCount,
  getNextTier,
} from "@/lib/hooks/use-usage";
import { InfoTooltip } from "@/components/ui/info-tooltip";
import { countOf } from "@/lib/utils/plural";

export const SCRIPT_HASH_QUOTA_EXPLANATION =
  "Browsers send one for each script your pages load when your policy includes 'report-sha256'. They count toward your monthly quota on every plan.";

interface UsageCardProps {
  onUpgrade?: () => void;
}

/**
 * Full usage details card for the billing settings page.
 * Shows usage meter, period breakdown, and upgrade prompt.
 */
export function UsageCard({ onUpgrade }: UsageCardProps) {
  const { data, isLoading, isError } = useUsage();

  if (isLoading) {
    return (
      <div className="rounded-lg border bg-card p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 w-32 rounded bg-muted" />
          <div className="h-3 w-full rounded bg-muted" />
          <div className="h-4 w-48 rounded bg-muted" />
        </div>
      </div>
    );
  }

  if (isError || !data) {
    return null;
  }

  const nextTier = getNextTier(data.plan);
  const showUpgradePrompt =
    (data.status === "warning" ||
      data.status === "soft_exceeded" ||
      data.status === "hard_exceeded") &&
    nextTier !== null;

  return (
    <div className="rounded-lg border bg-card p-6">
      {/* The period is the meter's: "Since Oct 1 · resets Nov 1, 2026". */}
      <h2 className="mb-4 text-lg font-semibold">Current usage</h2>

      <UsageMeter
        reportCount={data.reportCount}
        limit={data.limit}
        percentUsed={data.percentUsed}
        periodStart={data.periodStart}
        periodEnd={data.periodEnd}
        status={data.status}
      />

      {/* Part of the total, not beside it: every plan pays for these (task 264). */}
      {data.scriptHashReportCount > 0 && (
        <p
          className="mt-3 text-sm text-muted-foreground"
          data-testid="usage-script-hashes"
        >
          {countOf(data.reportCount, "report")},{" "}
          {formatReportCount(data.scriptHashReportCount)} of them script hashes
          <InfoTooltip content={SCRIPT_HASH_QUOTA_EXPLANATION} />
        </p>
      )}

      {/* Soft exceeded info */}
      {data.status === "soft_exceeded" && data.softExceededCount > 0 && (
        <div className="mt-4 flex items-start gap-3 rounded-md bg-orange-50 p-3 dark:bg-orange-900/20">
          <AlertTriangle className="h-5 w-5 text-orange-500 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-orange-800 dark:text-orange-200">
              {countOf(data.softExceededCount, "report")}{" "}
              {data.softExceededCount === 1 ? "is" : "are"} hidden
            </p>
            <p className="text-sm text-orange-700 dark:text-orange-300 mt-1">
              These reports are stored but won't appear in your dashboard.
              Upgrade to restore visibility.
            </p>
          </div>
        </div>
      )}

      {/* Upgrade prompt */}
      {showUpgradePrompt && onUpgrade && (
        <div className="mt-4 flex items-center justify-between rounded-md bg-muted/50 p-4">
          <div className="flex items-center gap-3">
            <TrendingUp className="h-5 w-5 text-muted-foreground" />
            <div>
              <p className="text-sm font-medium">Need more capacity?</p>
              <p className="text-sm text-muted-foreground">
                Upgrade to {nextTier} for higher limits
              </p>
            </div>
          </div>
          <Button size="sm" onClick={onUpgrade}>
            Upgrade
          </Button>
        </div>
      )}
    </div>
  );
}
